import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowDown,
  FaArrowRight,
  FaBoxOpen,
  FaCopy,
  FaGift,
  FaLink,
  FaMagic,
  FaPalette,
  FaReceipt,
  FaRibbon,
  FaSync,
  FaTicketAlt,
} from "react-icons/fa";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import CandyWrapCelebration from "./CandyWrapCelebration";
import { primeAnimationAudio } from "../../hooks/useAnimationSound";
import { useRadixConnect } from "../../../radix_connect";
import { useCandyWrapper } from "../../hooks/useCandyWrapper";
import "./WrapperStudioPage.css";

const QUICK_AMOUNTS = [1, 5, 10, 25];

const truncateValue = (value) => {
  if (!value || value.length < 18) {
    return value || "";
  }

  return `${value.slice(0, 12)}...${value.slice(-8)}`;
};

const formatTimestamp = (timestamp) => {
  try {
    return new Date(timestamp).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "Just now";
  }
};

const formatCandyAmount = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return "0";
  }

  if (Number.isInteger(numericValue)) {
    return numericValue.toLocaleString("en-US");
  }

  return numericValue.toLocaleString("en-US", {
    maximumFractionDigits: 2,
  });
};

const WrapperStudioPage = () => {
  const navigate = useNavigate();
  const gallerySectionRef = useRef(null);
  const lastCelebratedReceiptIdRef = useRef(null);
  const { selectedAccount } = useRadixConnect();
  const [celebrationReceipt, setCelebrationReceipt] = useState(null);
  const [showWrapCelebration, setShowWrapCelebration] = useState(false);
  const {
    selectedPair,
    wrapAmount,
    setWrapAmount,
    availablePairs,
    latestReceipt,
    sessionReceipts,
    balanceState,
    error,
    gameStatus,
    isWrapping,
    isConfigReady,
    hasWrapperComponentAddress,
    hasLiveWrapperConfig,
    isAmountValid,
    hasEnoughBalance,
    canSubmit,
    GAME_STATUS,
    selectPair,
    wrapCandy,
    componentState,
    refreshComponentState,
    refreshSelectedBalance,
    clearError,
    clearLatestReceipt,
    clearSessionReceipts,
  } = useCandyWrapper();

  const handleCopy = useCallback(async (value) => {
    if (!value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
    } catch (copyError) {
      console.error("Failed to copy wrapper value:", copyError);
    }
  }, []);

  const handleAmountChange = useCallback(
    (event) => {
      if (error) {
        clearError();
      }

      const nextValue = event.target.value.replace(/[^\d]/g, "");
      setWrapAmount(nextValue);
    },
    [clearError, error, setWrapAmount]
  );

  const handleQuickAmount = useCallback(
    (amount) => {
      if (error) {
        clearError();
      }

      setWrapAmount(String(amount));
    },
    [clearError, error, setWrapAmount]
  );

  const handleUseMax = useCallback(() => {
    if (error) {
      clearError();
    }

    if (balanceState.loading) {
      return;
    }

    const maxAmount = Math.floor(Number(balanceState.amount) || 0);
    if (maxAmount > 0) {
      setWrapAmount(String(maxAmount));
    }
  }, [balanceState.amount, balanceState.loading, clearError, error, setWrapAmount]);

  const handleStartWrap = useCallback(async () => {
    await primeAnimationAudio();
    const result = await wrapCandy();

    if (result?.success && result.receipt) {
      lastCelebratedReceiptIdRef.current = result.receipt.id;
      setCelebrationReceipt(result.receipt);
      setShowWrapCelebration(true);
    }
  }, [wrapCandy]);

  const handleCloseWrapCelebration = useCallback(() => {
    setShowWrapCelebration(false);
  }, []);

  const handleJumpToGallery = useCallback(() => {
    gallerySectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  const handleRefreshChain = useCallback(async () => {
    await refreshComponentState();
    await refreshSelectedBalance();
  }, [refreshComponentState, refreshSelectedBalance]);

  const handleSelectPair = useCallback(
    (pairAddress) => {
      if (error) {
        clearError();
      }

      if (latestReceipt) {
        clearLatestReceipt();
      }

      selectPair(pairAddress);
    },
    [clearError, clearLatestReceipt, error, latestReceipt, selectPair]
  );

  useEffect(() => {
    if (
      latestReceipt?.id &&
      gameStatus === GAME_STATUS.SUCCESS &&
      lastCelebratedReceiptIdRef.current !== latestReceipt.id
    ) {
      lastCelebratedReceiptIdRef.current = latestReceipt.id;
      setCelebrationReceipt(latestReceipt);
      setShowWrapCelebration(true);
    }
  }, [GAME_STATUS, gameStatus, latestReceipt]);

  const statusCopy = (() => {
    if (hasWrapperComponentAddress && componentState.loading) {
      return "Checking the wrapper on-chain...";
    }

    if (hasWrapperComponentAddress && componentState.error && componentState.hasVerifiedState) {
      return "Chain check hiccup. Showing the last verified wrapper state.";
    }

    if (hasWrapperComponentAddress && !componentState.componentExists) {
      return componentState.error || "Could not verify the wrapper on-chain.";
    }

    if (hasWrapperComponentAddress && !hasLiveWrapperConfig) {
      return "No wrapped candies are loaded right now.";
    }

    if (!hasLiveWrapperConfig) {
      return "Candy Wrapper is not available right now.";
    }

    switch (gameStatus) {
      case GAME_STATUS.SUBMITTING:
        return "Wallet time. Seal the stash.";
      case GAME_STATUS.SUCCESS:
        return "Wrapped. Check your wallet.";
      case GAME_STATUS.ERROR:
        return error || "Something interrupted the candy wrap.";
      default:
        if (!selectedPair) {
          return "Pick a candy below.";
        }

        if (!selectedAccount?.address) {
          return "Connect wallet.";
        }

        if (balanceState.loading) {
          return "Counting your stash...";
        }

        if (balanceState.error) {
          return "Could not verify your wallet balance right now.";
        }

        return `${selectedPair.beforeLabel} in. ${selectedPair.afterLabel} out.`;
    }
  })();

  const stationToneClass = (() => {
    if (!hasLiveWrapperConfig) {
      return "config-pending";
    }

    switch (gameStatus) {
      case GAME_STATUS.SUBMITTING:
        return "is-submitting";
      case GAME_STATUS.SUCCESS:
        return "is-success";
      case GAME_STATUS.ERROR:
        return "is-error";
      default:
        return "is-idle";
    }
  })();

  const selectedPairStyle = selectedPair
    ? {
        "--pair-accent": selectedPair.accent,
        "--pair-glow": selectedPair.glow,
        "--pair-gradient": selectedPair.versionGradient,
      }
    : undefined;

  const walletInventoryCopy = (() => {
    if (!selectedPair) {
      return "Pick one below";
    }

    if (!selectedAccount?.address) {
      return "Connect wallet";
    }

    if (balanceState.loading) {
      return "Counting...";
    }

    if (balanceState.error) {
      return "Balance unavailable";
    }

    return `${formatCandyAmount(balanceState.amount)} in wallet`;
  })();

  const chainStatus = (() => {
    if (componentState.loading) {
      return {
        label: "Checking",
        className: "is-checking",
      };
    }

    if (componentState.error && componentState.hasVerifiedState) {
      return {
        label: "Stale",
        className: "is-stale",
      };
    }

    if (componentState.componentExists) {
      return {
        label: "Verified",
        className: "is-verified",
      };
    }

    if (componentState.error) {
      return {
        label: "Retry",
        className: "is-error",
      };
    }

    return {
      label: "Missing",
      className: "is-missing",
    };
  })();

  return (
    <div className="wrapper-studio-page">
      <CandyWrapCelebration
        receipt={celebrationReceipt || latestReceipt}
        isVisible={showWrapCelebration}
        onClose={handleCloseWrapCelebration}
      />

      <section className="wrapper-hero">
        <div className="hero-copy">
          <Badge className="hero-badge">CANDY WRAPPER</Badge>
          <h1>Candy Wrapper</h1>
          <p className="hero-lead">
            Loose candy in. Fancy little wrapper out. Same count, less goblin
            energy.
          </p>
          <div className="hero-actions">
            <Button
              variant="default"
              size="lg"
              onClick={() => navigate("/dckslap/candyshop")}
            >
              <FaGift />
              <span>Grab Candy</span>
            </Button>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => navigate("/dckslap/candycombiner")}
            >
              <FaLink />
              <span>Fusion Soon</span>
            </Button>
          </div>
        </div>

        <div className="hero-display">
          <div className="display-arch" />
          <div className="display-ribbon display-ribbon-left" />
          <div className="display-ribbon display-ribbon-right" />
          <div className="display-box">
            <div className="display-box-inner">
              <div className="display-box-stage">Loose Candy</div>
              <div className="display-box-arrow">
                <FaArrowRight />
              </div>
              <div className="display-box-stage wrapped">Candy Wrap</div>
            </div>
            <div className="display-box-note">
              Win it, Wrap it, Trade it, Flex it
            </div>
          </div>
        </div>
      </section>

      {!hasLiveWrapperConfig && (
        <div className="wrapper-config-banner">
          <FaTicketAlt />
          <div>
              <strong>
                {hasWrapperComponentAddress
                  ? "Nothing loaded yet."
                  : "Candy Wrapper unavailable."}
              </strong>{" "}
            {hasWrapperComponentAddress
              ? "The wrapper is reachable, but there are no wrapped candies stocked yet."
              : "The wrapper connection is not available right now."}
          </div>
        </div>
      )}

      <section className="wrapper-steps">
        <div className="step-pill">
          <span className="step-index">1</span>
          <span>Pick candy</span>
        </div>
        <div className="step-pill">
          <span className="step-index">2</span>
          <span>Pick count</span>
        </div>
        <div className="step-pill">
          <span className="step-index">3</span>
          <span>Wrap it</span>
        </div>
      </section>

      <section className="wrapper-main-grid">
        <Card className="studio-notes-card">
          <CardHeader>
            <CardTitle>
              <FaPalette />
              <span>Why bother?</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="notes-list">
              <li>Loose candy is messy.</li>
              <li>Wrapped candy is the gold standard.</li>
            </ul>

            <div className="account-chip">
              <span>Connected Wallet</span>
              <strong>
                {selectedAccount?.address
                  ? truncateValue(selectedAccount.address)
                : "Connect to begin"}
              </strong>
            </div>

            <div className="studio-ledger-strip">
              <div className={`ledger-pill ${chainStatus.className}`}>
                <span>Chain</span>
                <strong>{chainStatus.label}</strong>
              </div>
              <div
                className={`ledger-pill ${
                  componentState.livePairCount > 0 ? "is-verified" : "is-missing"
                }`}
              >
                <span>Live pairs</span>
                <strong>
                  {componentState.loading
                    ? "..."
                    : formatCandyAmount(componentState.livePairCount)}
                </strong>
              </div>
            </div>

            <button
              type="button"
              className="studio-link-button"
              onClick={handleRefreshChain}
              disabled={componentState.loading}
            >
              {componentState.loading ? "Checking chain..." : "Refresh chain"}
            </button>

            <button
              type="button"
              className="studio-link-button studio-link-button-muted"
              onClick={() => navigate("/dckslap/candyshop")}
            >
              Need candy first?
            </button>
          </CardContent>
        </Card>

        <div className="studio-station-column">
          <section
            className={`wrapper-station ${stationToneClass}`}
            style={selectedPairStyle}
          >
            <div className="station-header">
              <div>
                <div className="station-kicker">Candy Wrap</div>
                <h2>{selectedPair ? selectedPair.displayName : "Select a pair"}</h2>
              </div>
              <Badge variant={isConfigReady ? "success" : "outline"}>
                {isConfigReady ? "READY" : "UNAVAILABLE"}
              </Badge>
            </div>

            <div className="station-body">
              <article className="identity-card before-card">
                <div className="identity-topline">Before</div>
                <h3>{selectedPair ? selectedPair.beforeLabel : "Loose Candy"}</h3>
                <p>
                  {selectedPair
                    ? selectedPair.beforeCopy
                    : "Pick a candy below and we will make it look less feral."}
                </p>
                <div className="identity-card-meta">
                  <span>Your stash</span>
                  <strong>{walletInventoryCopy}</strong>
                </div>
              </article>

              <div className="station-core">
                <div className="foil-drum">
                  <div className="foil-ring foil-ring-a" />
                  <div className="foil-ring foil-ring-b" />
                  <div className="foil-ring foil-ring-c" />
                  <div className="foil-center">
                    <FaRibbon />
                  </div>
                </div>

                <div className="amount-control">
                  <div className="amount-control-head">
                    <label htmlFor="wrap-amount">How many?</label>
                    <button
                      type="button"
                      className="gallery-jump-button"
                      onClick={handleJumpToGallery}
                    >
                      <FaArrowDown />
                      <span>Pick below</span>
                    </button>
                  </div>
                  <div className="wallet-count-chip">
                    <div className="wallet-count-copy">
                      <span>Selected candy</span>
                      <strong>{walletInventoryCopy}</strong>
                    </div>
                    <button
                      type="button"
                      className="wallet-count-action"
                      onClick={handleUseMax}
                      disabled={
                        !selectedAccount?.address ||
                        balanceState.loading ||
                        Boolean(balanceState.error) ||
                        Number(balanceState.amount) < 1
                      }
                    >
                      Use Max
                    </button>
                  </div>
                  <div className="amount-input-shell">
                    <input
                      id="wrap-amount"
                      inputMode="numeric"
                      value={wrapAmount}
                      onChange={handleAmountChange}
                      placeholder="1"
                    />
                    <span>units</span>
                  </div>
                  <div className="quick-amounts">
                    {QUICK_AMOUNTS.map((amount) => (
                      <button
                        key={amount}
                        type="button"
                        onClick={() => handleQuickAmount(amount)}
                        className={String(amount) === wrapAmount ? "is-active" : ""}
                      >
                        {amount}
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  variant="default"
                  size="xl"
                  type="button"
                  onClick={handleStartWrap}
                  disabled={!canSubmit}
                  className="wrap-trigger"
                >
                  {isWrapping ? (
                    <>
                      <FaSync className="spin-icon" />
                      <span>Wrapping...</span>
                    </>
                  ) : (
                    <>
                      <FaMagic />
                      <span>
                        {isConfigReady
                          ? "Wrap This Stash"
                          : hasLiveWrapperConfig
                            ? "Pick Candy"
                            : "Nothing Loaded"}
                      </span>
                    </>
                  )}
                </Button>
              </div>

              <article className="identity-card after-card">
                <div className="identity-topline">After</div>
                <h3>{selectedPair ? selectedPair.afterLabel : "Gift Wrap"}</h3>
                <p>
                  {selectedPair
                    ? selectedPair.afterCopy
                    : "Same candy, just dressed properly."}
                </p>
                <div className="identity-card-meta">
                  <span>Candy Wrap promise</span>
                  <strong>1:1 sealed output in the same wallet</strong>
                </div>
              </article>
            </div>

            <div className="station-footer">
              <div className="status-strip">
                <span className="status-label">Candy Wrap Status</span>
                <strong>{statusCopy}</strong>
                {selectedPair &&
                  isAmountValid &&
                  !hasEnoughBalance &&
                  !balanceState.loading && (
                  <em>Not enough balance for the current amount.</em>
                )}
              </div>
            </div>
          </section>

          <Card className="receipt-card">
            <CardHeader>
              <CardTitle>
                <FaReceipt />
                <span>Wrap Receipt</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {latestReceipt ? (
                <div className="receipt-shell">
                  <div className="receipt-hero">
                    <div>
                      <span className="receipt-label">Sealed Pair</span>
                      <h3>{latestReceipt.pairDisplayName}</h3>
                    </div>
                    <Badge variant="success">SUBMITTED</Badge>
                  </div>

                  <div className="receipt-grid">
                    <div>
                      <span className="receipt-label">Amount</span>
                      <strong>{latestReceipt.amount}</strong>
                    </div>
                    <div>
                      <span className="receipt-label">Wallet</span>
                      <strong>{truncateValue(latestReceipt.accountAddress)}</strong>
                    </div>
                    <div>
                      <span className="receipt-label">Wrapped At</span>
                      <strong>{formatTimestamp(latestReceipt.submittedAt)}</strong>
                    </div>
                    <div>
                      <span className="receipt-label">Intent Hash</span>
                      <strong>{truncateValue(latestReceipt.transactionIntentHash)}</strong>
                    </div>
                  </div>

                  <div className="receipt-card-preview">
                    <div className="preview-ticket-tag">SCREENSHOT CARD</div>
                    <div className="preview-ticket-body">
                      <span>{latestReceipt.beforeLabel}</span>
                      <FaArrowRight />
                      <span>{latestReceipt.afterLabel}</span>
                    </div>
                    <div className="preview-ticket-foot">
                      {latestReceipt.amount} wrapped for{" "}
                      {truncateValue(latestReceipt.accountAddress)}
                    </div>
                  </div>

                  <div className="receipt-actions">
                    <button
                      type="button"
                      onClick={() =>
                        handleCopy(latestReceipt.transactionIntentHash)
                      }
                    >
                      <FaCopy />
                      <span>Copy hash</span>
                    </button>
                    <button type="button" onClick={clearLatestReceipt}>
                      Clear
                    </button>
                  </div>
                </div>
              ) : (
                <div className="receipt-empty">
                  <FaBoxOpen />
                  <p>No fresh wrap yet.</p>
                  <span>Seal candy and the receipt lands here.</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="studio-side-card">
          <CardHeader>
            <CardTitle>
              <FaRibbon />
              <span>Latest Wraps</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="side-note">
              Your latest wraps, saved in this browser.
            </p>

            <div className="session-list">
              {sessionReceipts.length > 0 ? (
                sessionReceipts.map((receipt) => (
                  <div className="session-row" key={receipt.id}>
                    <div>
                      <strong>{receipt.pairDisplayName}</strong>
                      <span>
                        {receipt.amount} sealed on{" "}
                        {formatTimestamp(receipt.submittedAt)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleCopy(receipt.transactionIntentHash)}
                    >
                      <FaCopy />
                    </button>
                  </div>
                ))
              ) : (
                <div className="session-empty">
                  <p>Nothing wrapped yet.</p>
                  <span>Go make the shelf less embarrassing.</span>
                </div>
              )}
            </div>

            {sessionReceipts.length > 0 && (
              <button
                type="button"
                className="clear-session-button"
                onClick={clearSessionReceipts}
              >
                Clear shelf
              </button>
            )}
          </CardContent>
        </Card>
      </section>

      <section className="wrapper-gallery-section" ref={gallerySectionRef}>
        <div className="gallery-header">
          <div>
            <div className="gallery-badges">
              <Badge variant="outline">Wrapper Lineup</Badge>
              <Badge variant={hasLiveWrapperConfig ? "success" : "outline"}>
                {componentState.loading
                  ? "Checking chain"
                  : `${componentState.livePairCount} loaded`}
              </Badge>
            </div>
            <h2>Pick your candy</h2>
          </div>
          <p>
            {hasLiveWrapperConfig
              ? "Pick the loose candy you want to clean up."
              : "This list only shows candies that are actually loaded in the wrapper."}
          </p>
        </div>

        {availablePairs.length > 0 ? (
          <div className="wrapper-gallery-grid">
            {availablePairs.map((pair) => {
              const isSelected =
                pair.unwrappedResourceAddress === selectedPair?.unwrappedResourceAddress;

              return (
                <button
                  key={pair.unwrappedResourceAddress}
                  type="button"
                  className={`wrapper-pair-card ${isSelected ? "is-selected" : ""}`}
                  style={{
                    "--pair-accent": pair.accent,
                    "--pair-glow": pair.glow,
                    "--pair-gradient": pair.versionGradient,
                  }}
                  onClick={() => handleSelectPair(pair.unwrappedResourceAddress)}
                >
                  <div className="pair-card-topline">
                    <Badge variant="success">Loaded</Badge>
                    <span>{pair.version}</span>
                  </div>
                  <div className="pair-card-art" />
                  <div className="pair-card-copy">
                    <strong>{pair.displayName}</strong>
                    <span>{pair.shortDescription}</span>
                  </div>
                  <div className="pair-card-flow">
                    <span>{pair.beforeLabel}</span>
                    <FaArrowRight />
                    <span>{pair.afterLabel}</span>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="wrapper-gallery-empty">
            <FaBoxOpen />
            <p>No candies are loaded in the wrapper yet.</p>
            <span>Once stock is added on-chain, the live lineup will show up here.</span>
          </div>
        )}
      </section>
    </div>
  );
};

export default WrapperStudioPage;
